import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { API_URL } from '../../../config/api';
import { useAuth } from '../../../context/AuthContext';
import { useNotification } from '../../../context/NotificationContext';
import CancelOrderModal from '../../../components/CancelOrderModal';
import './Dashboard.css';

const Dashboard = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { showError } = useNotification();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updatingStatus, setUpdatingStatus] = useState({});
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelOrderId, setCancelOrderId] = useState(null);
  const [cancelOrderNumber, setCancelOrderNumber] = useState(null);
  const [orderSearch, setOrderSearch] = useState('');

  useEffect(() => {
    fetchStats();
    // Poll for new orders and updates every 5 seconds
    const interval = setInterval(fetchStats, 5000);
    return () => clearInterval(interval);
  }, []);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API_URL}/store-owner/dashboard/stats`);
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching dashboard stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (orderId, newStatus) => {
    if (newStatus === 'cancelled') {
      const order = [...(stats?.activeOrders || []), ...(stats?.newOrders || [])].find(o => o._id === orderId);
      setCancelOrderId(orderId);
      setCancelOrderNumber(order?.orderNumber);
      setShowCancelModal(true);
      return;
    }
    setUpdatingStatus(prev => ({ ...prev, [orderId]: true }));
    try {
      await axios.patch(`${API_URL}/store-owner/orders/${orderId}/status`, {
        status: newStatus
      });
      await fetchStats();
    } catch (error) {
      console.error('Error updating order status:', error);
      showError(t('admin.statusUpdateFailed'));
    } finally {
      setUpdatingStatus(prev => ({ ...prev, [orderId]: false }));
    }
  };

  const handleCancelOrderConfirm = async (cancellationMessage) => {
    if (!cancelOrderId) return;
    setUpdatingStatus(prev => ({ ...prev, [cancelOrderId]: true }));
    try {
      await axios.patch(`${API_URL}/store-owner/orders/${cancelOrderId}/status`, {
        status: 'cancelled',
        cancellationMessage
      });
      setShowCancelModal(false);
      setCancelOrderId(null);
      setCancelOrderNumber(null);
      await fetchStats();
    } catch (error) {
      console.error('Error cancelling order:', error);
      showError(t('admin.statusUpdateFailed'));
    } finally {
      setUpdatingStatus(prev => ({ ...prev, [cancelOrderId]: false }));
    }
  };

  const getStatusBadge = (status) => {
    const statusColors = {
      pending: { bg: '#f39c12', color: 'white' },
      confirmed: { bg: '#3498db', color: 'white' },
      preparing: { bg: '#9b59b6', color: 'white' },
      outForDelivery: { bg: '#e67e22', color: 'white' },
      delivered: { bg: '#27ae60', color: 'white' },
      cancelled: { bg: '#e74c3c', color: 'white' }
    };
    const colors = statusColors[status] || { bg: '#95a5a6', color: 'white' };
    return (
      <span 
        className="status-badge"
        style={{ 
          backgroundColor: colors.bg, 
          color: colors.color,
          padding: '4px 10px',
          borderRadius: '12px',
          fontSize: '11px',
          fontWeight: '600',
          textTransform: 'capitalize'
        }}
      >
        {status}
      </span>
    );
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  const formatCurrency = (amount) => {
    return `€${(amount || 0).toFixed(2)}`;
  };

  const filterOrdersBySearch = (orders, query) => {
    if (!query || !query.trim()) return orders || [];
    const q = query.trim().toLowerCase().replace(/\s/g, '');
    return (orders || []).filter((order) => {
      const orderNum = (order.orderNumber || order._id?.slice(-6) || '').toString().toLowerCase();
      const name = (order.user ? `${order.user.firstName || ''} ${order.user.lastName || ''}`.trim() : order.customerName || '').toLowerCase();
      const email = (order.user?.email || order.customerEmail || '').toLowerCase();
      const phone = (order.customerPhone || order.user?.phoneNumber || '').replace(/\s/g, '').toLowerCase();
      return orderNum.includes(q) || name.includes(q) || email.includes(q) || phone.includes(q);
    });
  };

  const filteredNewOrders = filterOrdersBySearch(stats?.newOrders, orderSearch);
  const filteredActiveOrders = filterOrdersBySearch(stats?.activeOrders, orderSearch);

  const getMaxValue = (data) => {
    if (!data || data.length === 0) return 1;
    return Math.max(...data.map(item => item.sales));
  };

  if (loading) {
    return <div className="loading-spinner">{t('admin.loadingDashboard')}</div>;
  }

  return (
    <div className="owner-dashboard">
      <h2>{t('admin.dashboardOverview')}</h2>
      
      {/* Stats Cards */}
      <div className="stats-grid">
        <div className="stat-card">
          <h3>{t('admin.todaysOrders')}</h3>
          <p className="stat-number">{stats?.todaysOrdersCount || 0}</p>
        </div>
        <div className="stat-card">
          <h3>{t('admin.todaysSales')}</h3>
          <p className="stat-number">{formatCurrency(stats?.todaysSales || 0)}</p>
        </div>
      </div>

      {/* Monthly Sales Chart */}
      {stats?.monthlySalesChart && stats.monthlySalesChart.length > 0 && (
        <div className="chart-container">
          <h3>{t('admin.thisMonthsSales')}</h3>
          <div className="chart-bars">
            {stats.monthlySalesChart.map((day, index) => {
              const maxSales = getMaxValue(stats.monthlySalesChart);
              return (
                <div key={index} className="chart-bar-container">
                  <div 
                    className="chart-bar monthly" 
                    style={{ height: `${(day.sales / maxSales) * 200}px` }}
                  >
                    <span className="chart-value">{formatCurrency(day.sales)}</span>
                  </div>
                  <span className="chart-label">{day.day}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="dashboard-orders-search">
        <label htmlFor="order-search" className="dashboard-search-label">
          <i className="fas fa-search"></i>
          <span>{t('admin.searchOrders') || 'Search orders'}</span>
        </label>
        <input
          id="order-search"
          type="text"
          className="dashboard-search-input"
          placeholder={t('admin.searchOrdersPlaceholder') || 'Order number, customer name, email, phone...'}
          value={orderSearch}
          onChange={(e) => setOrderSearch(e.target.value)}
        />
      </div>

      {/* Active Orders Section */}
      <div className="active-orders-section">
        <h3>{t('admin.activeOrders')}</h3>
        {filteredActiveOrders.length > 0 ? (
          <div className="orders-list">
            {filteredActiveOrders.map(order => (
              <div key={order._id} className="order-card">
                <div className="order-header">
                  <div>
                    <h4>{t('admin.orderNumber')} {order.orderNumber || order._id.slice(-6)}</h4>
                    <p className="order-customer">
                      <i className="fas fa-user"></i> {order.user ? `${order.user.firstName || ''} ${order.user.lastName || ''}`.trim() : (order.customerName || '—')}
                    </p>
                    <p className="order-time">
                      <i className="fas fa-clock"></i> {formatDate(order.updatedAt || order.createdAt)}
                    </p>
                    {(order.customerPhone || order.user?.phoneNumber) && (
                      <p className="order-phone">
                        <i className="fas fa-phone"></i> {order.customerPhone ?? order.user?.phoneNumber}
                      </p>
                    )}
                    {order.deliveryDriver && (
                      <p className="order-driver">
                        <i className="fas fa-truck"></i> {t('admin.driver')}: {order.deliveryDriver.firstName} {order.deliveryDriver.lastName}
                      </p>
                    )}
                  </div>
                  {getStatusBadge(order.status)}
                </div>
                
                <div className="order-details">
                  <div className="order-items">
                    <strong>{t('admin.items')}:</strong>
                    <ul>
                      {order.items?.map((item, idx) => (
                        <li key={idx} className="order-item-with-variations">
                          <div className="order-item-main">
                            <span className="item-quantity">{item.quantity}x</span>
                            <span className="item-name">{item.name}</span>
                            <span className="item-price">{formatCurrency(item.price * item.quantity)}</span>
                          </div>
                          {item.selectedVariations && (
                            <div className="order-item-variations">
                              {Array.isArray(item.selectedVariations) ? (
                                item.selectedVariations.map((variation, vIdx) => (
                                  <div key={vIdx} className="order-variation-group">
                                    <span className="order-variation-name">{variation.name}:</span>
                                    <div className="order-variation-options">
                                      {variation.selectedOptions?.map((option, oIdx) => (
                                        <span key={oIdx} className="order-variation-badge">{option}</span>
                                      ))}
                                    </div>
                                  </div>
                                ))
                              ) : (
                                Object.entries(item.selectedVariations).map(([variationName, selectedOptions], vIdx) => (
                                  <div key={vIdx} className="order-variation-group">
                                    <span className="order-variation-name">{variationName}:</span>
                                    <div className="order-variation-options">
                                      {Array.isArray(selectedOptions) ? (
                                        selectedOptions.map((option, oIdx) => (
                                          <span key={oIdx} className="order-variation-badge">{option}</span>
                                        ))
                                      ) : (
                                        <span className="order-variation-badge">{selectedOptions}</span>
                                      )}
                                    </div>
                                  </div>
                                ))
                              )}
                            </div>
                          )}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="order-total">
                    <strong>{t('admin.total')}: {formatCurrency(order.total)}</strong>
                  </div>
                  {order.deliveryAddress && (
                    <div className="order-address">
                      <strong>{t('admin.deliveryAddress')}:</strong> {order.deliveryAddress.street}, {order.deliveryAddress.city}{order.deliveryAddress.addressDetails ? ` — ${order.deliveryAddress.addressDetails}` : ''}
                    </div>
                  )}
                  {(order.restaurantInstructions || order.driverInstructions || order.customerInstructions) && (
                    <div className="order-instructions">
                      <strong>{t('admin.customerInstructionsSection')}:</strong>{' '}
                      {[order.restaurantInstructions, order.driverInstructions].filter(Boolean).join(' · ') || order.customerInstructions}
                    </div>
                  )}
                </div>

                <div className="order-actions">
                  {order.status === 'confirmed' && (
                    <button
                      className="btn-preparing"
                      onClick={() => handleStatusChange(order._id, 'preparing')}
                      disabled={updatingStatus[order._id]}
                    >
                      {updatingStatus[order._id] ? t('admin.updating') : t('admin.startPreparing')}
                    </button>
                  )}
                  {order.status === 'preparing' && (
                    <button
                      className="btn-out-for-delivery"
                      onClick={() => handleStatusChange(order._id, 'outForDelivery')}
                      disabled={updatingStatus[order._id]}
                    >
                      {updatingStatus[order._id] ? t('admin.updating') : t('admin.outForDelivery')}
                    </button>
                  )}
                  {order.status !== 'cancelled' && (
                    <button
                      className="btn-cancel"
                      onClick={() => handleStatusChange(order._id, 'cancelled')}
                      disabled={updatingStatus[order._id]}
                    >
                      {t('admin.cancelOrder')}
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="no-orders">
            <p>{t('admin.noActiveOrders')}</p>
          </div>
        )}
      </div>

      <CancelOrderModal
        show={showCancelModal}
        orderNumber={cancelOrderNumber}
        onConfirm={handleCancelOrderConfirm}
        onClose={() => { setShowCancelModal(false); setCancelOrderId(null); setCancelOrderNumber(null); }}
      />

      {/* New Orders Section */}
      <div className="new-orders-section">
        <h3>{t('admin.newOrders')}</h3>
        {filteredNewOrders.length > 0 ? (
          <div className="orders-list">
            {filteredNewOrders.map(order => (
              <div key={order._id} className="order-card">
                <div className="order-header">
                  <div>
                    <h4>{t('admin.orderNumber')} {order.orderNumber || order._id.slice(-6)}</h4>
<p className="order-customer">
                      <i className="fas fa-user"></i> {order.user ? `${order.user.firstName || ''} ${order.user.lastName || ''}`.trim() : (order.customerName || '—')}
                    </p>
                    <p className="order-time">
                      <i className="fas fa-clock"></i> {formatDate(order.createdAt)}
                    </p>
                    {(order.customerPhone || order.user?.phoneNumber) && (
                      <p className="order-phone">
                        <i className="fas fa-phone"></i> {order.customerPhone ?? order.user?.phoneNumber}
                      </p>
                    )}
                  </div>
                  {getStatusBadge(order.status)}
                </div>

                <div className="order-details">
                  <div className="order-items">
                    <strong>{t('admin.items')}:</strong>
                    <ul>
                      {order.items?.map((item, idx) => (
                        <li key={idx} className="order-item-with-variations">
                          <div className="order-item-main">
                            <span className="item-quantity">{item.quantity}x</span>
                            <span className="item-name">{item.name}</span>
                            <span className="item-price">{formatCurrency(item.price * item.quantity)}</span>
                          </div>
                          {item.selectedVariations && (
                            <div className="order-item-variations">
                              {Array.isArray(item.selectedVariations) ? (
                                // Handle array format
                                item.selectedVariations.map((variation, vIdx) => (
                                  <div key={vIdx} className="order-variation-group">
                                    <span className="order-variation-name">{variation.name}:</span>
                                    <div className="order-variation-options">
                                      {variation.selectedOptions?.map((option, oIdx) => (
                                        <span key={oIdx} className="order-variation-badge">{option}</span>
                                      ))}
                                    </div>
                                  </div>
                                ))
                              ) : (
                                // Handle object format (variation name as key)
                                Object.entries(item.selectedVariations).map(([variationName, selectedOptions], vIdx) => (
                                  <div key={vIdx} className="order-variation-group">
                                    <span className="order-variation-name">{variationName}:</span>
                                    <div className="order-variation-options">
                                      {Array.isArray(selectedOptions) ? (
                                        selectedOptions.map((option, oIdx) => (
                                          <span key={oIdx} className="order-variation-badge">{option}</span>
                                        ))
                                      ) : (
                                        <span className="order-variation-badge">{selectedOptions}</span>
                                      )}
                                    </div>
                                  </div>
                                ))
                              )}
                            </div>
                          )}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="order-total">
                    <strong>{t('admin.total')}: {formatCurrency(order.total)}</strong>
                  </div>
                  {order.deliveryAddress && (
                    <div className="order-address">
                      <strong>{t('admin.deliveryAddress')}:</strong> {order.deliveryAddress.street}, {order.deliveryAddress.city}{order.deliveryAddress.addressDetails ? ` — ${order.deliveryAddress.addressDetails}` : ''}
                    </div>
                  )}
                  {(order.restaurantInstructions || order.driverInstructions || order.customerInstructions) && (
                    <div className="order-instructions">
                      <strong>{t('admin.customerInstructionsSection')}:</strong>{' '}
                      {[order.restaurantInstructions, order.driverInstructions].filter(Boolean).join(' · ') || order.customerInstructions}
                    </div>
                  )}
                </div>

                <div className="order-actions">
                  {order.status === 'pending' && (
                    <>
                      <button
                        className="btn-accept"
                        onClick={() => handleStatusChange(order._id, 'confirmed')}
                        disabled={updatingStatus[order._id]}
                      >
                        {updatingStatus[order._id] ? t('admin.updating') : t('admin.accept')}
                      </button>
                      <button
                        className="btn-cancel"
                        onClick={() => handleStatusChange(order._id, 'cancelled')}
                        disabled={updatingStatus[order._id]}
                      >
                        {t('admin.cancel')}
                      </button>
                    </>
                  )}
                  {order.status === 'confirmed' && (
                    <button
                      className="btn-preparing"
                      onClick={() => handleStatusChange(order._id, 'preparing')}
                      disabled={updatingStatus[order._id]}
                    >
                      {updatingStatus[order._id] ? t('admin.updating') : t('admin.startPreparing')}
                    </button>
                  )}
                  {order.status === 'preparing' && (
                    <button
                      className="btn-out-for-delivery"
                      onClick={() => handleStatusChange(order._id, 'outForDelivery')}
                      disabled={updatingStatus[order._id]}
                    >
                      {updatingStatus[order._id] ? t('admin.updating') : t('admin.outForDelivery')}
                    </button>
                  )}
                  {order.status !== 'cancelled' && order.status !== 'pending' && (
                    <button
                      className="btn-cancel"
                      onClick={() => handleStatusChange(order._id, 'cancelled')}
                      disabled={updatingStatus[order._id]}
                    >
                      {t('admin.cancelOrder')}
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="no-orders">
            <p>{t('admin.noNewOrders')}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
